import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { WarningAltFilled20 } from '@carbon/icons-react';
import { OktaContext } from '@okta/okta-react';
import { Link, useLocationSearch } from '@rocketlane/rocket-router';
import { Divider } from 'antd';
import { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AuthenticationServiceProvider } from 'api/account';
import { LinkText, UserInfo } from 'components/atoms/form-elements';
import { BackgroundColor, InputBorder, InputFocus } from 'components/atoms/input/input';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { CustomerLogin } from 'components/molecules/session-setup/customer-login-link-generate';
import { BodyShort01, Label01, Text01 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common/company-logo';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { useIsSamlMandatory } from '../saml';
import './login.css';
import { CustomSignInWidget } from './custom-signin-widget';
import { ForgotPassword } from './forgot-password';
import { Container, Heading, HeadingContainer, HelperTitle, TextContainer, WelcomeHeading } from './login-layout';
import { SsoSamlButton } from './sso-saml-button';
import { useCognitoAuth } from './use-cognito-auth';
import { useLoginSsoCallbackError } from './use-login-sso-callback-error';
const OktaSignInWidget = lazyWithRetry(()=>import('./okta-signin-widget').then(({ SignInWidget })=>({
            default: SignInWidget
        })));
const HelperLink = styled(Link)`
  ${BodyShort01};
`;
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  & button {
    height: 50px;
  }
`;
const LinksContainer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;
const StyledDivider = styled(Divider)`
  span {
    transform: translateY(50%);
    font-size: var(--font-size-sm);
    color: var(--gray-50);
  }
`;
const WidgetContainer = styled.div`
  width: fit-content;
  position: relative;
`;
const SsoCallbackErrorAlert = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: 0 0 var(--spacing-05) 0;
  max-width: 400px;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    flex-shrink: 0;
    fill: var(--red-50);
  }
`;
const StyledSignInWidget = styled(OktaSignInWidget)`
  #okta-sign-in {
    margin-bottom: 0 !important;
    &.auth-container {
      &.main-container {
        box-shadow: none;
        border: none;
        margin-top: 0;
      }
      * {
        font-family: var(--font-family) !important;
      }
    }
    .auth-content {
      padding: 0;
    }
    .auth-footer {
      display: none;
    }
    label {
      ${Label01};
    }

    #okta-sign-in .o-form .input-fix {
      background: var(--gray-10) !important;
    }
    .o-form-fieldset {
      margin-bottom: var(--spacing-05) !important;

      :nth-child(1) {
        margin-bottom: 0px !important;
      }
      :nth-child(2) {
        margin-bottom: var(--spacing-03) !important;
      }
    }

    .o-form-content {
      padding-bottom: 0 !important;
    }

    .o-form-button-bar {
      padding-bottom: var(--spacing-03);
    }

    .o-form .okta-form-input-field {
      width: 100%;
      padding: 0 var(--spacing-05);
      ${InputBorder};
      ${BodyShort01};
      ${Text01};
      ${BackgroundColor};
      height: var(--input-height-lg);

      &.focused-input {
        ${InputFocus};
      }
    }

    /* For signin button*/
    #okta-signin-submit.button.button-primary {
      text-align: left;
      border-radius: 0;
      border-color: var(--blue-60);
      background: var(--blue-60);
      color: var(--white-100);
    }
  }

  .custom-checkbox {
    color: var(--gray-80) !important;
  }

  #okta-sign-in .focused-input {
    box-shadow: none;
  }
  #okta-sign-in .social-auth-button {
    padding: 0 0 0 var(--spacing-05);
    background: var(--blue-60);
    color: var(--white-100);
  }
`;
export const LoginPage = ()=>{
    var _data_okta_idps;
    var _data_okta, _useContext;
    const { data, isLoading, isError } = useAccountMetaQuery();
    const isSamlMandate = useIsSamlMandatory();
    const search = useLocationSearch();
    const params = new URLSearchParams(search);
    const customerParam = params.get('customer');
    const partnerParam = params.get('partner');
    const isSamlEnabled = useMemo(()=>{
        var _ref;
        return !!((_ref = data === null || data === void 0 ? void 0 : data.okta.idps) !== null && _ref !== void 0 ? _ref : []).filter((idp)=>idp.enabled).length;
    }, [
        data === null || data === void 0 ? void 0 : data.okta.idps
    ]);
    const isCognitoEnabled = (data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO;
    const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({
        data
    });
    const oktaAuth = (_useContext = useContext(OktaContext)) === null || _useContext === void 0 ? void 0 : _useContext.oktaAuth;
    const ssoCallbackErrorMessage = useLoginSsoCallbackError();
    return isLoading ? /*#__PURE__*/ _jsx(PageLoading, {}) : data && !isError ? /*#__PURE__*/ _jsx(Layout, {
        children: customerParam ? /*#__PURE__*/ _jsx(CustomerLogin, {}) : /*#__PURE__*/ _jsxs(Container, {
            children: [
                ssoCallbackErrorMessage && /*#__PURE__*/ _jsxs(SsoCallbackErrorAlert, {
                    color: "red",
                    children: [
                        /*#__PURE__*/ _jsx(WarningAltFilled20, {}),
                        ssoCallbackErrorMessage
                    ]
                }),
                /*#__PURE__*/ _jsxs(HeadingContainer, {
                    children: [
                        data && /*#__PURE__*/ _jsx(CompanyLogo, {
                            data: data.company
                        }),
                        /*#__PURE__*/ _jsxs(TextContainer, {
                            children: [
                                /*#__PURE__*/ _jsx(WelcomeHeading, {
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "welcome"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Heading, {
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "login.title",
                                        values: {
                                            companyName: data.company.companyName
                                        }
                                    })
                                })
                            ]
                        })
                    ]
                }),
                !customerParam && !partnerParam && isSamlEnabled && !isSamlMandate && /*#__PURE__*/ _jsxs(ButtonContainer, {
                    children: [
                        ((_data_okta_idps = data.okta.idps) !== null && _data_okta_idps !== void 0 ? _data_okta_idps : []).filter((idp)=>idp.enabled).map((idp)=>{
                            return /*#__PURE__*/ _jsx(SsoSamlButton, {
                                idp: idp,
                                googleSSOHandler: googleSSOHandler,
                                samlFlowHandler: samlFlowHandler,
                                isCognitoEnabled: isCognitoEnabled,
                                idpId: idp.idpId,
                                oktaAuth: oktaAuth
                            }, idp === null || idp === void 0 ? void 0 : idp.idpId);
                        }),
                        /*#__PURE__*/ _jsx(StyledDivider, {
                            orientation: "center",
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "sign_in_with_your_email"
                            })
                        })
                    ]
                }),
                partnerParam && /*#__PURE__*/ _jsx(HelperTitle, {
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "signing_in_as_partner"
                    })
                }),
                !customerParam && (isCognitoEnabled ? /*#__PURE__*/ _jsx(CustomSignInWidget, {
                    data: data
                }) : /*#__PURE__*/ _jsxs(WidgetContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(StyledSignInWidget, {
                            oktaMetaData: data.okta,
                            isSamlMandatory: isSamlMandate,
                            isSamlEnabled: isSamlEnabled,
                            isPartner: partnerParam
                        }),
                        /*#__PURE__*/ _jsx(ForgotPassword, {
                            isSamlMandate: isSamlMandate
                        })
                    ]
                })),
                !customerParam && !partnerParam && /*#__PURE__*/ _jsxs(LinksContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(HelperLink, {
                            to: `${RoutePaths.login}?customer=true`,
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "are_you_a_customer",
                                values: {
                                    companyName: data.company.companyName
                                }
                            })
                        }),
                        ' ',
                        isSamlMandate && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                "/",
                                ' ',
                                /*#__PURE__*/ _jsx(HelperLink, {
                                    to: `${RoutePaths.login}?partner=true`,
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "are_you_a_partner",
                                        values: {
                                            companyName: data.company.companyName
                                        }
                                    })
                                })
                            ]
                        })
                    ]
                }),
                customerParam || partnerParam ? /*#__PURE__*/ _jsx(UserInfo, {
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "forgot_password.cta_back",
                        values: {
                            a: (chunks)=>/*#__PURE__*/ _jsx(Link, {
                                    to: RoutePaths.login,
                                    children: /*#__PURE__*/ _jsx(LinkText, {
                                        children: chunks
                                    })
                                })
                        }
                    })
                }) : /*#__PURE__*/ _jsx(_Fragment, {})
            ]
        })
    }) : /*#__PURE__*/ _jsx(_Fragment, {});
};
